import { useFormik } from "formik";
import { useEffect, useState } from "react";
import TextInput from "../../components/inputs/textInput";
import CircleClockIcon from "../../icons/circleClockIcon";
import CircleEmailIcon from "../../icons/circleEmialIcon";
import CircleLocationIcon from "../../icons/circleLocationIcon";
import CirclePhoneIcon from "../../icons/circlePhoneIcon";
import TextArea from "../../components/inputs/textArea";
import Button from "../../components/buttons/button";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const ContactUs = () => {
  const { t } = useTranslation();

  // =========================
  // 📦 CONTACT INFO STATE
  // =========================
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // =========================
  // 📥 FETCH FROM API
  // =========================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch("https://ba7ary.draconiccode.com/public/api/web-data", {
          headers: {
            Accept: "application/json",
          },
        });

        const data = await res.json();
        setContactInfo(data);
      } catch (error) {
        console.log("Error loading contact info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // =========================
  // VALIDATION
  // =========================
  const yupSchema = yup.object().shape({
    name: yup.string().required(t("requiredField")),
    email: yup.string().email(t("invalidEmail")).required(t("requiredField")),
    phone: yup.string().required(t("requiredField")),
    message: yup.string().required(t("requiredField")),
  });

  // =========================
  // FORM
  // =========================
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
    validationSchema: yupSchema,

    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const response = await fetch(
          "https://ba7ary.draconiccode.com/public/api/contact-requests",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(values),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.log("Error:", errorData);
          alert("حدث خطأ أثناء إرسال الرسالة");
          return;
        }

        await response.json();

        alert("تم إرسال الرسالة بنجاح");
        resetForm();
      } catch (error) {
        console.log("Network Error:", error);
        alert("فشل الاتصال بالسيرفر");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <main>
      <article className="py-10">
        <div className="w-11/12 mx-auto space-y-5">
          <h1 className="text-[24px] md:text-[32px] text-mainBlue font-bold text-center">
            {t("contactUs")}
          </h1>

          <p className="md:text-[18px] text-mainGray text-center">
            {t("weAreHereToHelp")}
          </p>

          <div className="flex flex-wrap justify-between gap-4 md:flex-nowrap">

            {/* =========================
                CONTACT INFO
            ========================= */}
            <section className="w-full p-8 space-y-4 bg-white rounded-2xl shadow-mainShadow">
              <h2 className="font-semibold text-[32px] py-8">
                {t("contactInfo-")}
              </h2>

              {/* PHONE */}
              <div className="flex items-center gap-4">
                <CirclePhoneIcon />
                <div>
                  <h3 className="text-lg font-medium text-mainBlue">
                    {t("phone")}
                  </h3>

                  <p className="text-sm">
                    {contactInfo?.phone?.value || "+90 552 797 77 72"}
                  </p>

                  <p className="text-xs text-mainGray">
                    {contactInfo?.phone?.description || t("doNotHesitate")}
                  </p>
                </div>
              </div>

              {/* EMAIL */}
              <div className="flex items-center gap-4">
                <CircleEmailIcon />
                <div>
                  <h3 className="text-lg font-medium text-mainBlue">
                    {t("email")}
                  </h3>

                  <p className="text-sm">
                    {contactInfo?.email?.value || "info@megtlogistics.com"}
                  </p>

                  <p className="text-xs text-mainGray">
                    {contactInfo?.email?.description || t("generalInquiries")}
                  </p>
                </div>
              </div>

              {/* LOCATION */}
              <div className="flex items-center gap-4">
                <CircleLocationIcon />
                <div>
                  <h3 className="text-lg font-medium text-mainBlue">
                    {t("location")}
                  </h3>

                  <p className="text-sm">
                    {contactInfo?.location?.value || "Mersin, Turkey"}
                  </p>

                  <p className="text-xs text-mainGray">
                    {contactInfo?.location?.description ||
                      t("visitHeadquarters")}
                  </p>
                </div>
              </div>

              {/* HOURS */}
              <div className="flex items-center gap-4">
                <CircleClockIcon />
                <div>
                  <h3 className="text-lg font-medium text-mainBlue">
                    {t("workHours")}
                  </h3>

                  <p className="text-sm">
                    {contactInfo?.hours?.value || t("MonToFri")}
                  </p>

                  <p className="text-xs text-mainGray">
                    {contactInfo?.hours?.description || ""}
                  </p>
                </div>
              </div>
            </section>

            {/* =========================
                FORM
            ========================= */}
            <form
              onSubmit={formik.handleSubmit}
              className="w-full p-8 bg-white rounded-2xl shadow-mainShadow"
            >
              <h2 className="text-[24px] md:text-[32px] font-semibold my-8">
                {t("fillDetails")}
              </h2>

              <TextInput
                formik={formik}
                label={t("name")}
                placeholder={t("enterFullName")}
                name="name"
              />

              <TextInput
                formik={formik}
                label={t("email")}
                placeholder={t("enterEmail")}
                name="email"
              />

              <TextInput
                formik={formik}
                label={t("phone")}
                placeholder={t("enterPhoneNumber")}
                name="phone"
              />

              <TextArea
                formik={formik}
                label={t("message")}
                placeholder={t("message")}
                name="message"
              />

              <Button
                text={t("submit")}
                className="w-full text-center bg-mainRed"
              />
            </form>

          </div>
        </div>
      </article>
    </main>
  );
};

export default ContactUs;